import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useCountry } from "@/lib/CountryContext";
import { formatPrice, billingSuffix } from "@/lib/currency";

export function GroupCard({ group, to }) {
    return (
        <Link
            to={to}
            data-testid={`group-card-${group.id}`}
            className="group relative flex flex-col overflow-hidden rounded-2xl bg-white shadow-soft hover:shadow-lift transition-[transform,box-shadow] duration-300 hover:-translate-y-1"
        >
            <div className="h-48 w-full flex items-end justify-center pt-6 bg-white">
                <img
                    src={group.image}
                    alt={group.name}
                    className="h-44 object-contain"
                />
            </div>
            <div className="p-6 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-[0.3em] mb-2 text-gold-600">
                    {group.deal_count} live perks
                </div>
                <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-2xl text-navy-700 leading-tight">
                        {group.name}
                    </h3>
                    <ArrowUpRight
                        className="text-navy-500 group-hover:text-gold-600 transition-colors flex-shrink-0"
                        size={20}
                    />
                </div>
                <div className="text-navy-500 text-sm mt-1 mb-3">{group.tagline}</div>
                <p className="text-navy-500/80 text-sm leading-relaxed">
                    {group.description}
                </p>
            </div>
        </Link>
    );
}

export function DealCard({ deal, to, footer }) {
    const { country } = useCountry();
    return (
        <div
            data-testid={`deal-card-${deal.id}`}
            className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-soft hover:shadow-lift transition-[transform,box-shadow] duration-300 hover:-translate-y-1"
        >
            <div className="relative h-52 bg-white flex items-center justify-center overflow-hidden pt-4">
                <img
                    src={deal.image}
                    alt={deal.title}
                    className="h-44 object-contain transition-transform duration-500 group-hover:scale-105"
                />
                {deal.tag && (
                    <span className="absolute top-4 left-4 inline-flex items-center rounded-full bg-gold-500 text-navy-900 text-[10px] uppercase tracking-widest font-semibold px-3 py-1">
                        {deal.tag}
                    </span>
                )}
                {deal.savings_pct > 0 && (
                    <span className="absolute top-4 right-4 inline-flex items-center rounded-full bg-navy-700 text-white text-xs font-bold px-3 py-1">
                        Save {deal.savings_pct}%
                    </span>
                )}
            </div>
            <div className="p-6 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold-600 mb-2">
                    {deal.vendor}
                </div>
                <h3 className="font-display text-xl text-navy-700 leading-snug mb-2">
                    {deal.title}
                </h3>
                {deal.description && (
                    <p
                        data-testid={`deal-card-desc-${deal.id}`}
                        className="text-sm text-navy-500 leading-relaxed mb-3 line-clamp-2"
                    >
                        {deal.description}
                    </p>
                )}
                <div className="flex items-center gap-2 mb-4 text-xs text-navy-500">
                    <img
                        src="/pink_5_stars_large.png"
                        alt=""
                        className="h-3 object-contain"
                    />
                    <span>
                        {deal.rating.toFixed(1)} · {deal.reviews} reviews
                    </span>
                </div>
                <div className="mt-auto flex items-baseline gap-2">
                    {deal.hide_price_until_calculated ? (
                        <span
                            data-testid={`deal-card-price-hidden-${deal.id}`}
                            className="text-sm text-navy-500"
                        >
                            Get a quote to see pricing
                        </span>
                    ) : (
                        <>
                            <span className="font-display text-2xl text-navy-700">
                                {formatPrice(deal.price, deal.is_paid_USD ? "US" : country, { decimals: 2 })}
                                {billingSuffix(deal.billing_interval)}
                            </span>
                            {deal.original_price > deal.price && (
                                <span className="text-sm text-navy-500 line-through">
                                    {formatPrice(deal.original_price, deal.is_paid_USD ? "US" : country, { decimals: 2 })}
                                    {billingSuffix(deal.billing_interval)}
                                </span>
                            )}
                        </>
                    )}
                </div>
                <div className="mt-5">
                    {footer ??
                        (to ? (
                            <Link
                                to={to}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-navy-700 group-hover:text-gold-600"
                            >
                                View perk <ArrowUpRight size={16} />
                            </Link>
                        ) : null)}
                </div>
            </div>
        </div>
    );
}

export function KpiCard({ label, value, delta, icon: Icon, accent = "gold" }) {
    const color = accent === "gold" ? "text-gold-600" : "text-pinkstar";
    return (
        <div className="rounded-2xl bg-white shadow-soft p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="text-xs uppercase tracking-[0.3em] text-navy-500">
                    {label}
                </div>
                {Icon && <Icon size={18} className={color} strokeWidth={1.7} />}
            </div>
            <div className="font-display text-3xl text-navy-700">{value}</div>
            {delta && (
                <div className="text-xs mt-2 text-navy-500">{delta}</div>
            )}
        </div>
    );
}
